export type Persistence = 'persisted' | 'unpersisted';

export function createAuthAttempts<S>(deps: {
  snapshot: () => S;
  same: (a: S, b: S) => boolean;
  writesAllowed: () => boolean;
}): { begin(): (() => boolean) | null } {
  let latestAttempt = 0;
  return {
    begin() {
      if (!deps.writesAllowed()) return null;
      const session = deps.snapshot();
      const attempt = ++latestAttempt;
      return () => attempt === latestAttempt && deps.writesAllowed() && deps.same(session, deps.snapshot());
    },
  };
}

export function createSessionReload(deps: { reload: () => void }): {
  request(reason: 'storage' | 'stale' | 'upgrade'): void;
  track(save: Promise<Persistence>): void;
  markUnsaved(): void;
  clearUnsaved(): void;
  writesAllowed(): boolean;
  reloadNow(): void;
  onBlocked(listener: () => void): void;
} {
  let pending = false;
  let saving = 0;
  let unsaved = false;
  let blocked = false;
  let reloaded = false;
  const listeners = new Set<() => void>();

  function reloadOnce(): void {
    if (reloaded) return;
    reloaded = true;
    deps.reload();
  }

  function settle(): void {
    if (!pending || saving !== 0 || blocked || reloaded) return;
    if (unsaved) {
      blocked = true;
      for (const listener of [...listeners]) listener();
    } else {
      reloadOnce();
    }
  }

  function finish(result: Persistence): void {
    saving -= 1;
    if (result === 'unpersisted') unsaved = true;
    settle();
  }

  return {
    request(_reason) {
      pending = true;
      settle();
    },
    track(save) {
      saving += 1;
      void save.then(result => finish(result), () => finish('unpersisted'));
    },
    markUnsaved() {
      unsaved = true;
      settle();
    },
    clearUnsaved() {
      unsaved = false;
    },
    writesAllowed() {
      return !pending && !blocked;
    },
    reloadNow() {
      pending = true;
      reloadOnce();
    },
    onBlocked(listener) {
      if (listeners.has(listener)) return;
      listeners.add(listener);
      if (blocked) listener();
    },
  };
}
